import { useState, useRef } from 'react';
import axios from 'axios';
import { getSolarData, isPricingUnavailable, PRICING_UNAVAILABLE_MESSAGE } from '../apiCalls';
import { trackJourney } from '../utils/journey';
import { hasCalculationResult } from '../utils/calculationResult';

const isCanceled = (err) =>
    axios.isCancel(err) || err.code === 'ERR_CANCELED' || err.name === 'AbortError';

export const getCalculationErrorMessage = (err) =>
    typeof err?.friendlyMessage === 'string' && err.friendlyMessage.trim()
        ? err.friendlyMessage
        : isPricingUnavailable(err) ? PRICING_UNAVAILABLE_MESSAGE
            : 'Unable to calculate results. Please try again.';

export const withRetry = async (fn, retries = 1) => {
    try {
        return await fn();
    } catch (err) {
        const isTransient = !err.response || err.response.status >= 500;
        if (retries > 0 && isTransient && !isCanceled(err) && !isPricingUnavailable(err)) {
            await new Promise(resolve => setTimeout(resolve, 1000));
            return withRetry(fn, retries - 1);
        }
        throw err;
    }
};

export const useSolarCalculation = () => {
    const [results, setResults] = useState(null);
    const [isCalculating, setIsCalculating] = useState(false);
    const [error, setError] = useState(null);
    const [pricingUnavailable, setPricingUnavailable] = useState(false);
    const abortRef = useRef(null);
    const isCalculatingRef = useRef(false);

    const calculate = async (formValues) => {
        if (isCalculatingRef.current) return;
        isCalculatingRef.current = true;
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        setIsCalculating(true);
        setError(null);
        setResults(null);
        setPricingUnavailable(false);

        try {
            const response = await withRetry(
                () => getSolarData(formValues, { signal: controller.signal })
            );
            if (controller.signal.aborted) return null;
            if (!hasCalculationResult(response.data)) {
                throw new Error('Invalid calculation response');
            }
            setResults(response.data);
            trackJourney('calculation_completed');
            return response.data;
        } catch (err) {
            if (controller.signal.aborted || isCanceled(err)) return null;
            const message = getCalculationErrorMessage(err);
            setError(message);
            setResults(null);
            setPricingUnavailable(isPricingUnavailable(err));
            return null;
        } finally {
            setIsCalculating(false);
            isCalculatingRef.current = false;
        }
    };

    const reset = () => {
        abortRef.current?.abort();
        setResults(null);
        setError(null);
    };

    return { results, isCalculating, error, pricingUnavailable, calculate, reset };
};
